// Libs
import { ModelBase } from "@shoothill/core";
import * as MobX from "mobx";
import { observable } from "mobx";
import moment, { Moment } from "moment";
import { IsNotEmpty } from "class-validator";

// App
import { ApplicantDTO } from "../DTO/ApplicantDTO";
import { PersonalDetailsDTO } from "../DTO/PersonalDetailsDTO";
import { PreviousAddressDTO } from "../DTO/PreviousAddressDTO";
import { FamilyAddressDTO } from "../DTO/FamilyAddressDTO";
import { PersonalDetailsModel } from "Globals/Models/Domain/FormSections/PersonalDetails/PersonalDetailsModel";
import { EmploymentDetailsModel } from "Globals/Models/Domain/FormSections/EmploymentDetails/EmploymentDetailsModel";
import { IncomeDetailsModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsModel";
import { EmploymentDetailsDTO } from "../DTO/EmploymentDetailsDTO";
import { ArmedForcesProofDTO } from "../DTO/ArmedForcesProofDTO";
import { AuditedAccountDTO } from "../DTO/AuditedAccountDTO";
import { IncomeDetailDTO } from "Globals/Models/DTO/IncomeDetailDTO";
import { MoneyGifterDTO } from "Globals/Models/DTO/MoneyGifterDTO";
import { OutstandingLoanDTO } from "Globals/Models/DTO/OutstandingLoanDTO";
import { CreditCardDTO } from "Globals/Models/DTO/CreditCardDTO";
import { FinancialCommitmentDTO } from "Globals/Models/DTO/FinancialCommitmentDTO";
import { StatementDetailsModel } from "./FormSections/StatmentDetails/StatementDetailsModel";
import { StatementDetailDTO } from "../DTO/StatementDetailDTO";
import { PropertyDetailsModel } from "./FormSections/PropertyDetails/PropertyDetailsModel";
import { PropertyDetailDTO } from "../DTO/PropertyDetailDTO";
import { PropertyDetailPersonDTO } from "../DTO/PropertyDetailPersonDTO";
import { CurrentPropertyDetailsModel } from "./FormSections/CurrentPropertyDetails/CurrentPropertyDetailsModel";
import { CurrentPropertyDetailDTO } from "../DTO/CurrentPropertyDetailDTO";
import { NewPropertyDetailsModel } from "./FormSections/NewPropertyDetails/NewPropertyDetailsModel";
import { NewPropertyDetailDTO } from "../DTO/NewPropertyDetailDTO";
import { BackgroundInformationModel } from "./FormSections/BackgroundInformation/BackgroundInformationModel";
import { BackgroundInformationDTO } from "../DTO/BackgroundInformationDTO";
import { DeclarationModel } from "./FormSections/Declarations/DeclarationModel";
import { DeclarationDTO } from "../DTO/DeclarationDTO";
import { CreditReportModel } from "./FormSections/CreditReport/CreditReportModel";
import { SupportingInformationModel } from "./FormSections/SupportingInformation/SupportingInformationModel";
import { CreditReportDTO } from "../DTO/CreditReportDTO";
import { SupportingInformationDTO } from "../DTO/SupportingInformationDTO";
import { PaymentInformationDTO } from "../DTO/PaymentInformationDTO";
import { ParentCreditReportModel } from "./FormSections/CreditReport/ParentCreditReportModel";
import { ParentSupportingInformationModel } from "./FormSections/SupportingInformation/ParentSupportingInformationModel";
import { AmendmentsModel } from "./Admin/applicant/AmendmentsModel";
import { AmendmentModel } from "./Admin/applicant/AmendmentModel";
import { Amendment } from "../Amendment";

export class ApplicantModel extends ModelBase<ApplicantModel, ApplicantAndRelated> {
    @MobX.observable public id: string | null = null;
    @MobX.observable public formId: string = "";
    @MobX.observable public applicantNumber: number = 1;
    @MobX.observable public primaryApplicant: boolean = false;
    @MobX.observable public createdDate: Date | null = null;
    @MobX.observable public isDeleted: boolean = false;

    @observable
    public personalDetailsModel: PersonalDetailsModel = new PersonalDetailsModel();
    public employmentDetailsModel: EmploymentDetailsModel = new EmploymentDetailsModel();
    public incomeDetailsModel: IncomeDetailsModel = new IncomeDetailsModel();
    public statementDetailsModel: StatementDetailsModel = new StatementDetailsModel();
    public propertyDetailsModel: PropertyDetailsModel = new PropertyDetailsModel();
    public currentPropertyDetailsModel: CurrentPropertyDetailsModel = new CurrentPropertyDetailsModel();
    public newPropertyDetailsModel: NewPropertyDetailsModel = new NewPropertyDetailsModel();
    public backgroundInformationModel: BackgroundInformationModel = new BackgroundInformationModel();
    public declarationModel: DeclarationModel = new DeclarationModel();
    public parentCreditReportModel: ParentCreditReportModel = new ParentCreditReportModel();
    public parentSupportingInformationModel: ParentSupportingInformationModel = new ParentSupportingInformationModel();
    public amendmentsModel: AmendmentsModel = new AmendmentsModel();

    @MobX.action
    public fromDto = (model: ApplicantAndRelated): void => {
        if (model !== null && model !== undefined) {
            this.id = model.applicant.id;
            this.formId = model.applicant.formId;
            this.applicantNumber = model.applicant.applicantNumber;
            this.primaryApplicant = model.applicant.primaryApplicant;
            this.createdDate = model.applicant.createdDate;
            this.isDeleted = model.applicant.isDeleted;

            // Load personal details
            if (
                model.personalDetails != undefined &&
                model.personalDetails != null &&
                model.personalDetailPreviousAddresses != undefined &&
                model.personalDetailPreviousAddresses != null &&
                model.personalDetailFamilyAddresses != undefined &&
                model.personalDetailFamilyAddresses != null
            ) {
                let localPersonalDetailsModel: PersonalDetailsModel = new PersonalDetailsModel();
                localPersonalDetailsModel.fromDto({
                    personalDetails: model.personalDetails,
                    previousAddressModels: model.personalDetailPreviousAddresses,
                    familyAddressModels: model.personalDetailFamilyAddresses,
                });
                this.personalDetailsModel = localPersonalDetailsModel;
            }

            // Load employment details
            if (
                model.employmentDetails != undefined &&
                model.employmentDetails != null &&
                model.employmentDetailsArmedForcesProof != undefined &&
                model.employmentDetailsArmedForcesProof != null &&
                model.employmentDetailsAuditedAccount != undefined &&
                model.employmentDetailsAuditedAccount != null
            ) {
                let localEmploymentDetailsModel: EmploymentDetailsModel = new EmploymentDetailsModel();
                localEmploymentDetailsModel.fromDto({
                    employmentDetails: model.employmentDetails,
                    armedForcesProofModels: model.employmentDetailsArmedForcesProof,
                    auditedAccountModels: model.employmentDetailsAuditedAccount,
                });
                this.employmentDetailsModel = localEmploymentDetailsModel;
            }

            // Load income details
            if (
                model.incomeDetails != undefined &&
                model.incomeDetails != null &&
                model.incomeDetailsMoneyGifter != undefined &&
                model.incomeDetailsMoneyGifter != null &&
                model.incomeDetailsOutstandingLoan != undefined &&
                model.incomeDetailsOutstandingLoan != null &&
                model.incomeDetailsCreditCard != undefined &&
                model.incomeDetailsCreditCard != null &&
                model.incomeDetailsFinancialCommitment != undefined &&
                model.incomeDetailsFinancialCommitment != null
            ) {
                let localIncomeDetailsModel: IncomeDetailsModel = new IncomeDetailsModel();
                localIncomeDetailsModel.fromDto({
                    incomeDetail: model.incomeDetails,
                    moneyGifterModels: model.incomeDetailsMoneyGifter,
                    outstandingLoanModels: model.incomeDetailsOutstandingLoan,
                    creditCardModels: model.incomeDetailsCreditCard,
                    financialCommitmentModels: model.incomeDetailsFinancialCommitment,
                });
                this.incomeDetailsModel = localIncomeDetailsModel;
            }

            // Load statement details
            if (model.statementDetails != undefined && model.statementDetails != null) {
                let localStatementDetailsModel: StatementDetailsModel = new StatementDetailsModel();
                localStatementDetailsModel.fromDto({
                    statementDetail: model.statementDetails,
                });
                this.statementDetailsModel = localStatementDetailsModel;
            }

            // Load property details
            if (model.propertyDetails != undefined && model.propertyDetails != null) {
                let localPropertyDetailsModel: PropertyDetailsModel = new PropertyDetailsModel();
                localPropertyDetailsModel.fromDto({
                    propertyDetail: model.propertyDetails,
                    personModels: model.propertyDetailsPerson,
                });
                this.propertyDetailsModel = localPropertyDetailsModel;
            }

            // Load current property details
            if (model.currentPropertyDetails != undefined && model.currentPropertyDetails != null) {
                let localCurrentPropertyDetailsModel: CurrentPropertyDetailsModel = new CurrentPropertyDetailsModel();
                localCurrentPropertyDetailsModel.fromDto({
                    currentPropertyDetail: model.currentPropertyDetails,
                });
                this.currentPropertyDetailsModel = localCurrentPropertyDetailsModel;
            }

            // Load new property details
            if (model.newPropertyDetails != undefined && model.newPropertyDetails != null) {
                let localNewPropertyDetailsModel: NewPropertyDetailsModel = new NewPropertyDetailsModel();
                localNewPropertyDetailsModel.fromDto(model.newPropertyDetails);
                this.newPropertyDetailsModel = localNewPropertyDetailsModel;
            }

            // Load background information
            if (model.backgroundInformation != undefined && model.backgroundInformation != null) {
                let localBackgroundInformationModel: BackgroundInformationModel = new BackgroundInformationModel();
                localBackgroundInformationModel.fromDto(model.backgroundInformation);
                this.backgroundInformationModel = localBackgroundInformationModel;
            }

            // Declaration
            if (model.declaration != undefined && model.declaration != null) {
                let localDeclarationModel: DeclarationModel = new DeclarationModel();
                localDeclarationModel.fromDto(model.declaration);
                this.declarationModel = localDeclarationModel;
            }

            // Credit Report
            if (model.creditReport != undefined && model.creditReport != null) {
                let localcreditReport: ParentCreditReportModel = new ParentCreditReportModel();
                localcreditReport.fromDto(model.creditReport);
                this.parentCreditReportModel = localcreditReport;
            }
            // Supporting Information
            if (model.supportingInformation != undefined && model.supportingInformation != null) {
                let localsupportingInformation: ParentSupportingInformationModel = new ParentSupportingInformationModel();
                localsupportingInformation.fromDto(model.supportingInformation);
                this.parentSupportingInformationModel = localsupportingInformation;
            }
            // Amendments
            if (model.amendments != undefined && model.amendments != null) {
                let localAmendments: AmendmentsModel = new AmendmentsModel();
                localAmendments.fromDto(model.amendments);
                this.amendmentsModel = localAmendments;
            }
        }
    };
    @MobX.action
    public toDto = (): ApplicantDTO => {
        let model: ApplicantDTO = {
            id: this.id,
            formId: this.formId,
            applicantNumber: this.applicantNumber,
            primaryApplicant: this.primaryApplicant,
            createdDate: this.createdDate,
            isDeleted: this.isDeleted,
        };
        return model;
    };
}

// Extend interface to accept new form sections
interface ApplicantAndRelated {
    applicant: ApplicantDTO;

    //Personal details
    personalDetails: PersonalDetailsDTO | undefined;
    personalDetailPreviousAddresses: PreviousAddressDTO[] | undefined;
    personalDetailFamilyAddresses: FamilyAddressDTO[] | undefined;

    //Employment details
    employmentDetails: EmploymentDetailsDTO | undefined;
    employmentDetailsArmedForcesProof: ArmedForcesProofDTO[] | undefined;
    employmentDetailsAuditedAccount: AuditedAccountDTO[] | undefined;

    //Income details
    incomeDetails: IncomeDetailDTO | undefined;
    incomeDetailsMoneyGifter: MoneyGifterDTO[] | undefined;
    incomeDetailsOutstandingLoan: OutstandingLoanDTO[] | undefined;
    incomeDetailsCreditCard: CreditCardDTO[] | undefined;
    incomeDetailsFinancialCommitment: FinancialCommitmentDTO[] | undefined;

    //Statement details
    statementDetails: StatementDetailDTO | undefined;

    //Property Details
    propertyDetails: PropertyDetailDTO | undefined;
    propertyDetailsPerson: PropertyDetailPersonDTO[] | undefined;

    //Current property Details
    currentPropertyDetails: CurrentPropertyDetailDTO | undefined;

    //New property Details
    newPropertyDetails: NewPropertyDetailDTO | undefined;

    //New background information
    backgroundInformation: BackgroundInformationDTO | undefined;

    //Declaration
    declaration: DeclarationDTO | undefined;

    //Credit Report
    creditReport: CreditReportDTO[] | undefined;

    //Supporting Information
    supportingInformation: SupportingInformationDTO[] | undefined;

    //Supporting Information
    amendments: Amendment[] | undefined;
}
